import type { AnchorHTMLAttributes } from "react";
import type { Components } from "react-markdown";

/* Timestamp citations: the minutes, action items and Personalized details put
 * "[00:12:34]" after a statement, the moment in the recording it comes from
 * (checked on the server against the meeting's length — src/minutes/citations.py).
 * Each one shows as a small time that plays the recording from there, like the
 * times in the transcript. With nothing to play, it's just the time. */

const CITATION = /\[(?:(\d{1,2}):)?(\d{1,2}):(\d{2})\]/g;
const LINK = "#t=";

function secondsOf(hours: string | undefined, minutes: string, seconds: string): number {
  return Number(hours ?? 0) * 3600 + Number(minutes) * 60 + Number(seconds);
}

/** 4:05, or 1:56:03 once a meeting passes the hour — "116:03" reads badly. */
export function clock(seconds: number): string {
  const whole = Math.floor(seconds);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const secs = String(whole % 60).padStart(2, "0");
  return hours ? `${hours}:${String(minutes).padStart(2, "0")}:${secs}` : `${minutes}:${secs}`;
}

export function Cite({ seconds, onSeek }: { seconds: number; onSeek?: (seconds: number) => void }) {
  if (!onSeek) return <span className="cite">{clock(seconds)}</span>;
  return (
    <button type="button" className="cite" onClick={() => onSeek(seconds)} title={`Play from ${clock(seconds)}`}>
      {clock(seconds)}
    </button>
  );
}

/** Plain text with its citations as times. */
export function Cited({ text, onSeek }: { text: string; onSeek?: (seconds: number) => void }) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(CITATION)) {
    const at = match.index ?? 0;
    // The space before a citation goes; the chip has its own margin.
    parts.push(text.slice(last, at).replace(/\s+$/, ""));
    parts.push(<Cite key={at} seconds={secondsOf(match[1], match[2], match[3])} onSeek={onSeek} />);
    last = at + match[0].length;
  }
  parts.push(text.slice(last));
  return <>{parts}</>;
}

/** "00:12:34" (an action item's time) as seconds, or null. */
export function timeOf(value: string | null | undefined): number | null {
  const match = new RegExp(CITATION.source).exec(`[${value ?? ""}]`);
  return match ? secondsOf(match[1], match[2], match[3]) : null;
}

/** Markdown with its citations as links to "#t=<seconds>", which `citeLinks`
 * below draws as times. */
export function withCiteLinks(markdown: string): string {
  return markdown.replace(CITATION, (_all, hours, minutes, seconds) =>
    `[${clock(secondsOf(hours, minutes, seconds))}](${LINK}${secondsOf(hours, minutes, seconds)})`);
}

/** react-markdown components drawing those links as times; any other link as it was. */
export function citeLinks(onSeek?: (seconds: number) => void): Components {
  return {
    a: ({ href, children, node: _node, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }) =>
      href?.startsWith(LINK) ? (
        <Cite seconds={Number(href.slice(LINK.length))} onSeek={onSeek} />
      ) : (
        <a href={href} {...rest}>{children}</a>
      ),
  };
}
